import { useEffect, useState } from 'react';

/**
 * Check if user prefers reduced motion
 */
export const usePrefersReducedMotion = (): boolean => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return prefersReducedMotion;
};

/**
 * ARIA live region announcer
 */
export class LiveAnnouncer {
  private static instance: LiveAnnouncer;
  private liveRegion: HTMLElement;

  private constructor() {
    this.liveRegion = this.createLiveRegion();
  }

  static getInstance(): LiveAnnouncer {
    if (!LiveAnnouncer.instance) {
      LiveAnnouncer.instance = new LiveAnnouncer();
    }
    return LiveAnnouncer.instance;
  }

  private createLiveRegion(): HTMLElement {
    let liveRegion = document.getElementById('aria-live-region');

    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = 'aria-live-region';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.style.position = 'absolute';
      liveRegion.style.left = '-10000px';
      liveRegion.style.width = '1px';
      liveRegion.style.height = '1px';
      liveRegion.style.overflow = 'hidden';
      document.body.appendChild(liveRegion);
    }

    return liveRegion;
  }

  announce(message: string, priority: 'polite' | 'assertive' = 'polite') {
    this.liveRegion.setAttribute('aria-live', priority);
    this.liveRegion.textContent = message;

    // Clear the message after a short delay to allow for repeated announcements
    setTimeout(() => {
      this.liveRegion.textContent = '';
    }, 1000);
  }
}

/**
 * Generate unique IDs for accessibility
 */
export const generateA11yId = (prefix: string): string => {
  return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Accessibility constants
 */
export const A11Y_MESSAGES = {
  QUESTION_CREATED: 'Question created successfully',
  QUESTION_UPDATED: 'Question updated successfully',
  QUESTION_DELETED: 'Question deleted successfully',
  QUESTION_REORDERED: 'Question order changed',
  VALIDATION_ERROR: 'Please fix validation errors before saving',
  AUTO_SAVE_SUCCESS: 'Changes saved automatically',
  AUTO_SAVE_FAILED: 'Auto-save failed, please save manually',
  DRAG_START: 'Started dragging question',
  DRAG_END: 'Finished dragging question',
  EDITING_STARTED: 'Entered editing mode',
  EDITING_CANCELLED: 'Cancelled editing',
  LOADING_MORE: 'Loading more questions',
  ALL_LOADED: 'All questions loaded',
};

/**
 * Color contrast utilities
 */
export const getContrastRatio = (color1: string, color2: string): number => {
  // Simple contrast ratio calculation
  // In a real implementation, you'd want a more comprehensive solution
  const getLuminance = (color: string): number => {
    // Basic luminance calculation
    const rgb = color.match(/\d+/g);
    if (!rgb) return 0;

    const [r, g, b] = rgb.map((c) => {
      const channel = parseInt(c) / 255;
      return channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };

  const lum1 = getLuminance(color1);
  const lum2 = getLuminance(color2);
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);

  return (brightest + 0.05) / (darkest + 0.05);
};
